// Sentry loaded from CDN (script tag in index.html) to avoid Rollup chunk TDZ issues
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const Sentry = (window as any).Sentry as any

// Sentry configuration for TraderTekno
export const initSentry = () => {
  if (!Sentry) return // CDN not loaded yet

  // Only initialize in production or when explicitly enabled
  const isProduction = import.meta.env.PROD
  const sentryEnabled = import.meta.env.VITE_SENTRY_ENABLED === 'true'

  if (!isProduction && !sentryEnabled) {
    return
  }

  const dsn = import.meta.env.VITE_SENTRY_DSN as string
  if (!dsn) {
    return
  }

  Sentry.init({
    dsn,
    environment: (import.meta.env.VITE_ENVIRONMENT as string) || 'development',

    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration()
    ],

    tracesSampleRate: isProduction ? 0.1 : 1.0,
    sampleRate: 1.0,
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: isProduction ? 1.0 : 0,
    release: `tradertekno@${import.meta.env.VITE_APP_VERSION || '1.0.0'}`,

    allowUrls: [
      /tradertekno\.trade/,
      /tradertekno\.com/,
      /tradertekno.*\.pages\.dev/,
      /localhost/,
    ],

    beforeSend(event: any) {
      if (event.exception) {
        const error = event.exception.values?.[0]
        if (error?.value?.includes('Non-Error promise rejection captured')) {
          return null
        }
        if (error?.value?.includes('Script error')) {
          return null
        }
      }

      if (import.meta.env.VITE_DEMO_MODE === 'true') {
        return null
      }

      return event
    },

    initialScope: {
      tags: {
        platform: 'web',
        product: 'tradertekno'
      }
    }
  })
}

export const setSentryUser = (user: {
  id: string
  email?: string
  username?: string
  subscription?: string
}) => {
  Sentry?.setUser({
    id: user.id,
    ...(user.email && { email: user.email }),
    ...(user.username && { username: user.username }),
    ...(user.subscription && { subscription: user.subscription })
  })
}

// Alias for dynamic import wrappers
export const setSentryUserAsync = setSentryUser

export const clearSentryUser = () => {
  Sentry?.setUser(null)
}

export const clearSentryUserAsync = clearSentryUser

export const setSentryContext = (context: {
  signalsFollowed?: number
  lastSignalAction?: string
  subscriptionTier?: string
  tradingSession?: string
}) => {
  Sentry?.setContext('trading', context)
}

export const captureSignalEvent = (action: string, signalData: {
  signalId: string
  symbol: string
  confidence: number
  direction: 'NAIK' | 'TURUN'
}) => {
  Sentry?.addBreadcrumb({
    category: 'signal',
    message: `Signal ${action}`,
    level: 'info',
    data: signalData
  })
}

export const capturePerformanceMetric = (metric: string, value: number, unit = 'ms') => {
  Sentry?.addBreadcrumb({
    category: 'performance',
    message: `${metric}: ${value}${unit}`,
    level: 'info',
    data: { metric, value, unit }
  })
}

export const captureBusinessError = (error: string, context?: Record<string, unknown>) => {
  Sentry?.withScope((scope: any) => {
    scope.setTag('type', 'business_error')
    scope.setLevel('warning')
    if (context) {
      scope.setExtras(context)
    }
    Sentry.captureMessage(error)
  })
}

export const captureError = (error: Error, context?: {
  component?: string
  action?: string
  userId?: string
  extra?: Record<string, unknown>
}) => {
  Sentry?.withScope((scope: any) => {
    if (context?.component) {
      scope.setTag('component', context.component)
    }
    if (context?.action) {
      scope.setTag('action', context.action)
    }
    if (context?.userId) {
      scope.setUser({ id: context.userId })
    }
    if (context?.extra) {
      scope.setExtras(context.extra)
    }

    Sentry.captureException(error)
  })
}

export default Sentry
